<script setup lang="ts">
  import { Form } from 'vee-validate'
  import { onMounted } from 'vue'
  import * as yup from 'yup'
  import type { QuestionnaireAnswerInput } from '@profilpublic/api-contract/questionnaire'
  import { QUESTIONNAIRE_DOC_FILE_PREFIX } from '@profilpublic/api-contract/questionnaire'
  import type { ApplyDocuments, ApplyJobFragment, ApplyQuestionnaireFragment, SelectOption } from '~/types/apply'
  import { resolveMotivationQuestions } from '~/utils/employer'
  import coverLetterSchema from '~/utils/schema/cover-letter-schema'
  import questionnaireSchema, { questionnaireFieldName } from '~/utils/schema/questionnaire-schema'

  const props = withDefaults(
    defineProps<{
      job?: ApplyJobFragment | null
      questionnaire?: ApplyQuestionnaireFragment
      resumeDeposit?: boolean // /depot-cv creates a candidate profile without a job
      spontaneous?: boolean // Career site application without a job
      spontaneousTypeLabel?: string
    }>(),
    { job: null, questionnaire: null, resumeDeposit: false, spontaneous: false, spontaneousTypeLabel: undefined }
  )

  const emit = defineEmits<{
    next: [values: ApplyDocuments]
    manual: [values: ApplyDocuments]
  }>()

  const theme = useCareerTheme()
  const { user } = useUser()
  const initialValues = computed(() => {
    const employer = (user.value as { candidate?: { incognitoEmployer?: number | { id?: number } | null } | null } | null)?.candidate
      ?.incognitoEmployer
    return {
      ...(props.resumeDeposit ? { incognitoEmployer: typeof employer === 'object' && employer !== null ? employer.id : (employer ?? null) } : {}),
      ...(props.spontaneous ? { searchedSector: null } : {})
    }
  })
  const primaryStyle = computed(() => (theme.value.primary ? { color: theme.value.primary } : undefined))
  const primaryButtonStyle = computed(() =>
    theme.value.primary
      ? {
          borderColor: theme.value.primary,
          color: theme.value.text || theme.value.primary,
          '--button-hover-color': theme.value.primary
        }
      : undefined
  )
  const linkStyle = computed(() =>
    theme.value.primary
      ? {
          textDecorationColor: theme.value.primary,
          '--link-hover': theme.value.primary
        }
      : undefined
  )

  const DOCUMENT_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.oasis.opendocument.text',
    'image/jpeg',
    'image/png'
  ]
  const DOCUMENT_MAX_SIZE = 5 * 1000 * 1000

  const letterMode = computed(() => (props.spontaneous ? 'file' : props.job?.letterMode || 'file'))
  const mandatoryLetter = computed(() => props.job?.mandatoryLetter)

  // Mode "both": letter upload and questions sit side by side, the candidate may fill either or both
  const showLetterChoice = computed(() => letterMode.value === 'both' && mandatoryLetter.value)
  const questionsOpen = ref(false)

  const letterUploadRendered = computed(() => letterMode.value !== 'questions')

  const questionsRendered = computed(() => !props.spontaneous && letterMode.value !== 'file')
  const questionsVisible = computed(
    () => letterMode.value === 'questions' || (letterMode.value === 'both' && (!showLetterChoice.value || questionsOpen.value))
  )
  const questionsRequired = computed(() => mandatoryLetter.value && letterMode.value === 'questions')

  const questionnaireItems = computed(() => (props.questionnaire?.enabled ? (props.questionnaire.items ?? []) : []))

  const FILE_OPTS = { types: DOCUMENT_TYPES, maxSize: DOCUMENT_MAX_SIZE }

  const questionCount = computed(() => resolveMotivationQuestions(props.job?.employer).length)

  const letterSchemaPart = computed(() =>
    coverLetterSchema(mandatoryLetter.value && letterMode.value === 'file', letterMode.value as string, FILE_OPTS, questionCount.value)
  )

  const schema = computed(() =>
    yup
      .object({
        cv: yupFile(FILE_OPTS).required('${label} requis').label('CV'),
        otherFile: yupFile(FILE_OPTS).label('Autre document').nullable(),
        ...(props.resumeDeposit ? { incognitoEmployer: yup.number().nullable() } : {}),
        ...(props.spontaneous ? { searchedSector: yup.number().typeError('Secteur requis').required('Secteur requis') } : {})
      })
      .concat(letterSchemaPart.value)
      .concat(questionnaireSchema(questionnaireItems.value))
  )

  const manual = ref(false)
  const incognitoEmployerTouched = ref(false)
  const sectorOptions = ref<SelectOption[]>([])

  onMounted(async () => {
    if (!props.spontaneous) return
    const sectors = await findAll('sectors', { fields: ['id', 'name'], sort: { name: 'asc' } })
    sectorOptions.value = sectors.map(({ id, name }) => ({ label: name, value: id }))
  })

  const title = computed(() => (props.resumeDeposit ? 'Je dépose mon CV' : props.spontaneous ? 'Candidature spontanée' : 'Je postule'))
  const cvLabel = computed(() => (props.resumeDeposit ? '+ Importez votre CV' : '+ CV'))

  const questionsIntro = computed(() =>
    letterMode.value === 'questions' ? 'Mot de motivation :' : showLetterChoice.value ? '' : 'Vous nʼavez pas de lettre de motivation ?'
  )

  const onSubmit = (values: Record<string, unknown>) => {
    const casted = schema.value.cast(values, { stripUnknown: true }) as ApplyDocuments & Record<string, unknown>
    if (props.resumeDeposit) casted.incognitoEmployerTouched = incognitoEmployerTouched.value

    const answers: QuestionnaireAnswerInput[] = []
    const attachedFiles: File[] = []
    for (const item of questionnaireItems.value) {
      const value = casted[questionnaireFieldName(item)]
      if (item.kind === 'document') {
        if (value) {
          const file = value as File
          const extension = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : ''
          attachedFiles.push(new File([file], `${QUESTIONNAIRE_DOC_FILE_PREFIX}${item.id}${extension}`, { type: file.type }))
        }
      } else if (value !== undefined && value !== null && value !== '') {
        answers.push({ itemId: item.id, answer: item.kind === 'closed' ? value === 'true' : (value as string) })
      }
    }
    const cleaned = Object.fromEntries(Object.entries(casted).filter(([key]) => !key.startsWith('questionnaireAnswer'))) as ApplyDocuments
    if (answers.length) cleaned.questionnaireAnswers = answers
    if (attachedFiles.length) cleaned.attachedFiles = attachedFiles

    if (manual.value) emit('manual', cleaned)
    else emit('next', cleaned)
    manual.value = false
  }

  const onIncognitoEmployerChange = (value: number | null, setFieldValue: (field: string, value: unknown) => void) => {
    incognitoEmployerTouched.value = true
    setFieldValue('incognitoEmployer', value)
  }

  const onInvalidSubmit = () => {
    manual.value = false
  }
</script>

<template>
  <div class="mx-auto w-full max-w-lg">
    <h1 class="text-center font-special text-3xl font-bold" :style="primaryStyle">{{ title }}</h1>
    <p class="mt-1 text-center text-lg" v-if="resumeDeposit">Attirez l'attention des recruteurs publics</p>
    <p class="mt-1 text-center text-lg text-zinc-500" v-else-if="spontaneousTypeLabel">{{ spontaneousTypeLabel }}</p>

    <Form
      class="mt-6 space-y-4"
      v-slot="{ errors, values, isSubmitting, submitCount, setFieldValue }"
      :initial-values="initialValues"
      :validation-schema="schema"
      @submit="onSubmit"
      @invalid-submit="onInvalidSubmit"
    >
      <FormUploadButton name="cv" :label="cvLabel" :accept="DOCUMENT_TYPES.join(',')" :error="errors.cv" :required="!showLetterChoice" />

      <template v-if="showLetterChoice">
        <div class="flex flex-col gap-2 text-sm sm:grid sm:grid-cols-[1fr_auto_1fr] sm:items-start sm:gap-3">
          <FormUploadButton name="letter" label="+ Lettre de motivation" :accept="DOCUMENT_TYPES.join(',')" :error="errors.letter" />

          <span class="self-center text-zinc-500 sm:self-start sm:py-[13px]">et/ou</span>

          <button
            class="choice-btn flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-3 font-bold duration-300"
            type="button"
            :class="
              errors.letter
                ? 'border-red-400 text-red-500'
                : questionsOpen
                  ? 'choice-btn--filled border-viking-400 text-viking-600'
                  : 'choice-btn--empty border-zinc-300 text-tarawera-900 hover:border-viking-400 hover:text-viking-500'
            "
            :style="theme.primary ? { '--upload-accent': theme.primary, '--upload-text': theme.text } : undefined"
            @click="questionsOpen = !questionsOpen"
          >
            <Icon class="flex-shrink-0" name="pen" />
            <span class="min-w-0 truncate">Questions de motivation</span>
          </button>
        </div>

        <p class="text-xs text-zinc-500">Formats : pdf, word (.doc, .docx), .odt, .jpeg, .png | Taille max : 5 Mo</p>

        <FormUploadButton name="otherFile" label="+ Autre document" :accept="DOCUMENT_TYPES.join(',')" :error="errors.otherFile" />
      </template>

      <template v-else>
        <div class="grid gap-4" :class="letterUploadRendered ? 'grid-cols-2' : 'grid-cols-1'">
          <FormUploadButton
            v-if="letterUploadRendered"
            name="letter"
            label="+ Lettre de motivation"
            :accept="DOCUMENT_TYPES.join(',')"
            :error="errors.letter"
          />
          <FormUploadButton name="otherFile" label="+ Autre document" :accept="DOCUMENT_TYPES.join(',')" :error="errors.otherFile" />
        </div>

        <p class="text-xs text-zinc-500">Formats : pdf, word (.doc, .docx), .odt, .jpeg, .png | Taille max : 5 Mo</p>
      </template>

      <div class="pt-2" v-if="spontaneous">
        <FormField
          id="searchedSector"
          name="searchedSector"
          type="select"
          custom
          with-autocomplete
          label="Dans quel secteur ?"
          placeholder="Choisissez une famille de métier"
          :options="sectorOptions"
          :error="errors.searchedSector"
          required
        />
      </div>

      <div class="space-y-1 pt-2" v-if="resumeDeposit">
        <label class="block font-bold">Je souhaite passer incognito auprès de :</label>
        <FormEmployerSearch
          :model-value="values.incognitoEmployer"
          placeholder="Recherchez votre employeur actuel"
          @update:model-value="v => onIncognitoEmployerChange(v, setFieldValue)"
        />
        <p class="text-sm text-zinc-500">Votre profil restera invisible pour cet employeur dans la CVthèque.</p>
      </div>

      <div class="mt-4 space-y-2" v-if="questionsRendered" v-show="questionsVisible">
        <p class="text-sm font-bold" v-if="questionsIntro">{{ questionsIntro }}</p>
        <FormCoverLetter
          :job="job"
          :errors="errors"
          :values="values"
          :required="questionsRequired"
          :submit-count="submitCount"
          :show-only-questions="true"
          :hide-toggle="showLetterChoice"
          :link-color="theme.primary || ''"
        />
        <p class="text-sm text-zinc-500" v-if="showLetterChoice">Une lettre au format PDF sera générée à partir de vos réponses.</p>
      </div>

      <div class="mt-4" v-if="questionnaireItems.length">
        <FormQuestionnaireFields :items="questionnaireItems" :errors="errors" :submit-count="submitCount" />
      </div>

      <p class="text-xs italic text-zinc-500">
        En cliquant sur Suivant, vous acceptez de pré-remplir automatiquement le formulaire grâce à notre outil assisté par IA. Vous pourrez vérifier
        et modifier les informations avant de valider votre {{ resumeDeposit ? 'dépôt' : 'candidature' }}.
      </p>

      <div class="flex flex-col items-center gap-2 pt-2">
        <Button
          :class="theme.primary ? '!bg-white hover:!text-[color:var(--button-hover-color)]' : ''"
          :color="theme.primary ? 'secondary' : 'primary'"
          :disabled="isSubmitting"
          type="submit"
          :style="primaryButtonStyle"
          >Suivant</Button
        >

        <button
          class="text-sm underline decoration-2 underline-offset-4 duration-300"
          :class="theme.primary ? 'hover:text-[color:var(--link-hover)]' : 'hover:text-viking-500'"
          type="submit"
          :style="linkStyle"
          @click="manual = true"
        >
          Remplir manuellement
        </button>
      </div>
    </Form>
  </div>
</template>

<style scoped>
  .choice-btn--filled {
    border-color: var(--upload-accent, #58d9db);
    color: var(--upload-accent, #0d9488);
  }

  .choice-btn--empty {
    color: var(--upload-text, #1f2937);
  }

  .choice-btn--empty:hover {
    border-color: var(--upload-accent, #58d9db);
    color: var(--upload-accent, #14b8a6);
  }
</style>
